type ChargeType = 'fixed' | 'percent';

type CalculationType =
  | 'per-night'
  | 'per-person'
  | 'per-reservation'
  | 'per-guest-per-night'
  | 'per-guest-per-reservation'
  | 'per-pet-per-night'
  | 'per-pet-per-reservation';

export interface Tax {
  uuid: string;
  name: string;
  description: string;
  tax_identifier: string;
  charge_type: ChargeType;
  calculation_type: CalculationType;
  value: number;
  enabled: boolean;
  created_at: string;
  updated_at: string;
  properties: number[];
  property_list?: { name: string; picture: string }[];
  subjects: string[];
  max_number_of_nights?: number | null;
}

export const DEFAULT_CALCULATION_TYPE = 'per-reservation';

export const PricesAndFees = {
  fees: [
    {
      key: 'cleaning_fee',
      label: 'Cleaning fee',
      fixed: true,
      percentage: false,
      calculationTypes: [
        {
          label: 'per stay',
          value: 'per-reservation',
          fixed: true,
          percent: false,
        },
      ],
    },
    {
      key: 'linens_fee',
      label: 'Linens fee',
      fixed: true,
      percentage: false,
      calculationTypes: [
        {
          label: 'per person',
          value: 'per-person',
          fixed: true,
          percent: false,
        },
        {
          label: 'per stay',
          value: 'per-reservation',
          fixed: true,
          percent: false,
        },
      ],
    },
    {
      key: 'resort_fee',
      label: 'Resort fee',
      fixed: true,
      percentage: true,
      calculationTypes: [
        {
          label: 'per stay',
          value: 'per-reservation',
          fixed: true,
          percent: true,
        },
        {
          label: 'per person',
          value: 'per-person',
          fixed: true,
          percent: false,
        },
        {
          label: 'per night',
          value: 'per-night',
          fixed: true,
          percent: false,
        },
        {
          label: 'per guest per night',
          value: 'per-guest-per-night',
          fixed: true,
          percent: false,
        },
      ],
    },
    {
      key: 'management_fee',
      label: 'Management fee',
      fixed: true,
      percentage: true,
      calculationTypes: [
        {
          label: 'per stay',
          value: 'per-reservation',
          fixed: true,
          percent: true,
        },
        {
          label: 'per person',
          value: 'per-person',
          fixed: true,
          percent: false,
        },
        {
          label: 'per night',
          value: 'per-night',
          fixed: true,
          percent: false,
        },
        {
          label: 'per guest per night',
          value: 'per-guest-per-night',
          fixed: true,
          percent: false,
        },
      ],
    },

    {
      key: 'community_fee',
      label: 'Community fee',
      fixed: true,
      percentage: true,
      calculationTypes: [
        {
          label: 'per stay',
          value: 'per-reservation',
          fixed: true,
          percent: true,
        },
        {
          label: 'per person',
          value: 'per-person',
          fixed: true,
          percent: false,
        },
        {
          label: 'per night',
          value: 'per-night',
          fixed: true,
          percent: false,
        },
        {
          label: 'per guest per night',
          value: 'per-guest-per-night',
          fixed: true,
          percent: false,
        },
      ],
    },
    {
      key: 'pet_fee',
      label: 'Pet fee',
      description: 'Charge this amount per pet per stay; does not include service animals',
      descriptionFeatureFlag: 'Does not include service animals',
      fixed: true,
      percentage: false,
      calculationTypes: [
        {
          label: 'per stay',
          value: 'per-reservation',
          fixed: true,
          percent: false,
        },
        {
          label: 'per night',
          value: 'per-night',
          fixed: true,
          percent: false,
        },
        {
          label: 'per pet per stay',
          value: 'per-pet-per-reservation',
          fixed: true,
          percent: false,
        },
        {
          label: 'per pet per night',
          value: 'per-pet-per-night',
          fixed: true,
          percent: false,
        },
      ],
    },
  ],
  securityDeposits: [
    {
      key: 'security_deposit',
    },
    {
      key: 'direct_security_deposit',
    },
  ],

  discounts: [
    {
      key: 'weekly_discount',
      label: 'Weekly discount',
      description: 'Applies for any stays of 7 nights or longer.',
    },
    {
      key: 'monthly_discount',
      label: 'Monthly discount',
      description: 'Applies for any stays of 28 nights or longer.',
    },
    {
      key: 'early_bird_discount',
      label: 'Early bird discount',
      description: 'Applies for any stays booked 1 to 24 months before arrival.',
    },
    {
      key: 'last_minute_discount',
      label: 'Last minute discount',
      description: 'Applies for any stays booked 1 to 28 days before arrival.',
    },
  ],
  occupancyBased: [
    {
      key: 'price_per_extra_person',
      label: 'Extra guest fee',
      description: 'Charge this amount per extra guest, per night.',
      fixed: true,
      percentage: false,
    },
    {
      key: 'guests_included',
      label: 'Guests included',
      description: 'These guests are included in the base fee.',
      fixed: false,
      percentage: false,
    },
    {
      key: 'maximum_occupancy',
      label: 'Maximum occupancy',
      description: '',
      fixed: false,
      percentage: false,
    },
  ],
  basePrice: [
    {
      key: 'base_price',
      label: 'Nightly rate',
      description: 'This is the nightly rate of the property.',
      fixed: true,
      percentage: false,
    },
    {
      key: 'weekend_price',
      label: 'Weekend rate',
      description: 'This is a custom weekend price for Friday and Saturday nights.',
      fixed: true,
      percentage: false,
    },
  ],
};

// These group keys are used to know which group should show the "saving" status
export const PricesAndFeesGroups = Object.keys(PricesAndFees);

export const RatePlanGroup = 'primaryRatePlan';
export const ListingMarkupsGroup = 'listingMarkups';
